import { useEffect, useState } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { Button, IconButton, List, ListItem, ListItemText, TextField } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import { updateNotificationsConfig } from '../../services/api/pharmacies.endpoints';
import { Pharmacy, WhatsappNotifications } from '../../Models';

// Extended type for internal use with stable ID
type WhatsappNotificationWithId = WhatsappNotifications & {
    id: string;
};

interface WhatsappNotificationsProps {
    user: Pharmacy;
}

export function WhatsappNotificationsComponent({ user }: WhatsappNotificationsProps) {
    const [originalContacts, setOriginalContacts] = useState<WhatsappNotificationWithId[]>([]);
    const [editableContacts, setEditableContacts] = useState<WhatsappNotificationWithId[]>([]);
    const [isGlobalEditMode, setIsGlobalEditMode] = useState(false);
    const [contactErrors, setContactErrors] = useState<{
        [key: number]: { name: boolean; phone: boolean };
    }>({});

    // Validation functions
    const validateName = (name: string): boolean => name.trim().length > 0;

    const validatePhoneNumber = (phone: string): boolean => {
        const numericChars = phone.replace(/\D/g, '');
        return numericChars.length >= 10;
    };

    // Check if all inputs are valid
    const isAllValid = (): boolean =>
        editableContacts.every((contact, index) => {
            const errors = contactErrors[index];
            if (errors?.name || errors?.phone) {
                return false;
            }
            return validateName(contact.name) && validatePhoneNumber(contact.phone_number);
        });

    // Check if there are any changes compared to original
    const hasChanges = (): boolean => {
        if (originalContacts.length !== editableContacts.length) {
            return true;
        }

        return editableContacts.some((contact, index) => {
            const original = originalContacts[index];
            return (
                !original ||
                contact.name !== original.name ||
                contact.phone_number !== original.phone_number
            );
        });
    };

    useEffect(() => {
        if (user?.notifications_configuration?.whatsapp_notifications) {
            const contacts = user.notifications_configuration.whatsapp_notifications.map(
                (contact, index) => ({
                    ...contact,
                    id: `existing-${index}-${contact.name}-${contact.phone_number}`,
                }),
            );
            setOriginalContacts(contacts);
            setEditableContacts(contacts);
        }
    }, [user]);

    const handleToggleEditMode = () => {
        if (isGlobalEditMode) {
            // Cancel editing - revert to original
            setEditableContacts([...originalContacts]);
            setContactErrors({});
        }
        setIsGlobalEditMode(!isGlobalEditMode);
    };

    const handleAddContact = () => {
        if (!isGlobalEditMode) {
            setIsGlobalEditMode(true);
        }
        const newContact = {
            name: '',
            phone_number: '+54 9 11',
            id: `new-${Date.now()}-${Math.random()}`,
        };
        setEditableContacts([...editableContacts, newContact]);

        // Initialize validation errors for the new contact
        const newIndex = editableContacts.length;
        const updatedErrors = { ...contactErrors };
        updatedErrors[newIndex] = {
            name: !validateName(newContact.name),
            phone: !validatePhoneNumber(newContact.phone_number),
        };
        setContactErrors(updatedErrors);
    };

    const handleDeleteContact = (index: number) => {
        const updatedContacts = [...editableContacts];
        updatedContacts.splice(index, 1);
        setEditableContacts(updatedContacts);

        // Update error indices
        const updatedErrors: { [key: number]: { name: boolean; phone: boolean } } = {};
        Object.keys(contactErrors).forEach((key) => {
            const keyIndex = parseInt(key, 10);
            if (keyIndex < index) {
                updatedErrors[keyIndex] = contactErrors[keyIndex];
            } else if (keyIndex > index) {
                updatedErrors[keyIndex - 1] = contactErrors[keyIndex];
            }
        });
        setContactErrors(updatedErrors);
    };

    const handleUpdateContact = (index: number, field: 'name' | 'phone_number', value: string) => {
        const updatedContacts = [...editableContacts];
        updatedContacts[index] = {
            ...updatedContacts[index],
            [field]: value,
        };
        setEditableContacts(updatedContacts);

        // Update validation errors for this contact
        const updatedErrors = { ...contactErrors };
        if (!updatedErrors[index]) {
            updatedErrors[index] = { name: false, phone: false };
        }

        if (field === 'name') {
            updatedErrors[index].name = !validateName(value);
        } else if (field === 'phone_number') {
            updatedErrors[index].phone = !validatePhoneNumber(value);
        }

        setContactErrors(updatedErrors);
    };

    const handleSaveAllChanges = async () => {
        if (!isAllValid()) {
            console.error('Todos los contactos deben tener nombre y número de teléfono válidos');
            return;
        }

        try {
            await updateNotificationsConfiguration(editableContacts);
            setOriginalContacts([...editableContacts]);
            setIsGlobalEditMode(false);
            setContactErrors({});
        } catch (error) {
            console.error('Error updating notifications configuration:', error);
        }
    };

    const updateNotificationsConfiguration = async (
        notifications: WhatsappNotificationWithId[],
    ) => {
        try {
            // Strip id field before sending to API
            const notificationsForApi: WhatsappNotifications[] = notifications.map(
                ({ id, ...contact }) => contact,
            );
            const config = {
                whatsapp_notifications: notificationsForApi,
            };
            await updateNotificationsConfig(config);
        } catch (error) {
            console.error('Error updating notifications configuration:', error);
        }
    };

    return (
        <Grid xs={12}>
            <Typography variant="h6" gutterBottom>
                Notificaciones de WhatsApp
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Los siguientes números recibirán notificaciones vía WhatsApp cuando nuevos pedidos
                ingresen a Delfar.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Es importante incluir código de país y código de área con + (ej: +54 9 11...)
            </Typography>

            <List>
                {editableContacts.map((contact, index) => (
                    <ListItem
                        key={contact.id}
                        secondaryAction={
                            isGlobalEditMode && (
                                <IconButton edge="end" onClick={() => handleDeleteContact(index)}>
                                    <DeleteIcon />
                                </IconButton>
                            )
                        }>
                        {isGlobalEditMode ? (
                            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                                <TextField
                                    label="Nombre"
                                    value={contact.name}
                                    onChange={(e) =>
                                        handleUpdateContact(index, 'name', e.target.value)
                                    }
                                    size="small"
                                    sx={{ width: '25%' }}
                                    error={contactErrors[index]?.name || false}
                                    helperText={
                                        contactErrors[index]?.name ? 'El nombre es requerido' : ''
                                    }
                                />
                                <TextField
                                    label="Número de teléfono"
                                    value={contact.phone_number}
                                    onChange={(e) =>
                                        handleUpdateContact(index, 'phone_number', e.target.value)
                                    }
                                    size="small"
                                    sx={{ width: '50%' }}
                                    error={contactErrors[index]?.phone || false}
                                    helperText={
                                        contactErrors[index]?.phone
                                            ? 'Debe tener al menos 10 dígitos'
                                            : ''
                                    }
                                />
                            </Stack>
                        ) : (
                            <ListItemText primary={contact.name} secondary={contact.phone_number} />
                        )}
                    </ListItem>
                ))}
            </List>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddContact}>
                    Agregar número
                </Button>

                {!isGlobalEditMode ? (
                    <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={handleToggleEditMode}
                        disabled={editableContacts.length === 0}>
                        Editar
                    </Button>
                ) : (
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={handleToggleEditMode}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveAllChanges}
                            color="primary"
                            disabled={!isAllValid() || !hasChanges()}>
                            Guardar
                        </Button>
                    </Stack>
                )}
            </Stack>
        </Grid>
    );
}
